import { useEffect, useMemo, useState } from "react";
import { reportsApiDataSource, ReportsApiError, ZohoReportDTO } from "../../data/ReportsApiDataSource";
import { ZohoReportSlug } from "../config/reportsNavigation";
import { resolvePreset } from "../config/dateRanges";
import { ReportFilters } from "../components/ReportFilterBar";

interface State {
  data: ZohoReportDTO | null;
  loading: boolean;
  error: string | null;
}

/** Fetches live from Zoho whenever the slug or the resolved filters change. */
export const useZohoReport = (slug: ZohoReportSlug, filters: ReportFilters, hasBasis: boolean) => {
  const [state, setState] = useState<State>({ data: null, loading: true, error: null });
  const [tick, setTick] = useState(0);

  const range = useMemo(
    () => resolvePreset(filters.preset, { from: filters.from, to: filters.to }),
    [filters.preset, filters.from, filters.to]
  );
  const cashBased = hasBasis && filters.basis === "cash";
  const ready = Boolean(range.from && range.to) && range.from <= range.to;

  useEffect(() => {
    if (!ready) return;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    reportsApiDataSource
      .fetchReport(
        slug,
        { filter_by: filters.preset, from_date: range.from, to_date: range.to, cash_based: cashBased },
        controller.signal
      )
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        const message =
          e instanceof ReportsApiError
            ? [e.message, ...Object.values(e.fieldErrors)].join(" ")
            : e instanceof Error
              ? e.message
              : "Something went wrong.";
        setState((s) => ({ ...s, loading: false, error: message }));
      });
    return () => controller.abort();
  }, [slug, filters.preset, range.from, range.to, cashBased, ready, tick]);

  return { ...state, range, retry: () => setTick((t) => t + 1) };
};
